import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { format} from "date-fns";
import { db } from "../config/firebase";
import {
  // collection,
  // query,
  // where,
  // getDocs,
  // addDoc,
  // deleteDoc,
  doc,
  updateDoc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import checkCertificateExpiration from "./checkCertificateDomain";
import { getAuth, getIdToken } from "firebase/auth";

import { CertificateResponse, RootCertificate, IntermediateCertificate } from "./checkCertificateDomain";
interface Certificate  extends CertificateResponse {
  id?: string;
  name: string;
  serial_number: string;
  domain: string;
  days_remaining: number;
  expired: boolean;
  issuer: string;
  subject: string;
  intermediate_cert?: [IntermediateCertificate] | null;
  root_cert?: RootCertificate | null;
  subject_alternative_names: string[];
  issued_at: string;
  expires_at: string;
  public_key: string;
  chain_length: string,
  last_checked: string,
  signature_algorithm: string,
  added_by: string;
  date_added: string;
  expiry_date: string;
  status: string;
  pem: string;
  version: number;
}

// interface NewCertificate {
//         domain: string;
//         serial_number: string,
//         status: string,
//         expired: boolean,
//         expires_at: string,
//         issuer: string,
//         last_checked: string,
//         days_remaining: number,
//         date_added: string,
//         expiry_date: string,
//         name: string,
//         issued_at: string,
//         added_by:  string,
//       };

interface SubscriptionData {

certificate_limit: number;
plan: string;
plan_id: string
plan_status: string;
}
interface UserData {
  userId: string;
  email: string;
  notifications: {
    email?: boolean;
    sms?: boolean;
    slack?: boolean;
    teams?: boolean;
    weekly_summary?: boolean;
    email_last_sent?: string;
    sms_last_sent?: string;
    slack_last_sent?: string;
    teams_last_sent?: string;
    weekly_summary_last_sent?: string;
  };
  slack_webhook?: string;
  sms_number?: string;
  teams_webhook?: string;
  certificates: Certificate[];
  plan?: string;
  certificate_limit?: number;
  last_payment_date?: string;
  created_at: string;
  subscription?: SubscriptionData;
}

export const useDomains = () => {
  const { user } = useAuth();
  const [domains, setDomains] = useState<Certificate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load domains from Firestore
  useEffect(() => {
    const loadDomains = async () => {
      if (!user) {
        setDomains([]);
        setIsLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", user.id);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          // Create new user document with default values
          const userData: UserData = {
            userId: user.id,
            email: user.email || '',
            plan: "free",
            certificate_limit: 3,
            created_at: new Date().toISOString(),
            notifications: {
              email: false,
              sms: false,
              slack: false,
              teams: false,
              weekly_summary: false,
              teams_last_sent: "",
              email_last_sent: "",
              sms_last_sent: "",
              slack_last_sent: "",
              weekly_summary_last_sent: "",
            },
            subscription: {
              certificate_limit: 3,
              plan: "free",
              plan_id: "free",
              plan_status: "active",
            },
            slack_webhook: "",
            sms_number: "",
            teams_webhook: "",
            certificates: [],
          };
          await setDoc(userRef, userData);
          setDomains([]);
        } else {
          const userData = userDoc.data() as UserData;
          setDomains(userData.certificates || []);
        }
      } catch (err) {
        setError("Failed to load domains");
        console.error("Error loading domains:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadDomains();
  }, [user]);
  

  const sendDomainAddedEmail = async (domain: Certificate) => {
    if (!user?.email) return;
    const auth = getAuth();
    const thisUser = auth.currentUser;
    if (!thisUser) {
      throw new Error("User not authenticated");
    }

    const token = await getIdToken(thisUser);


    try {
      const response = await fetch("https://remindme-planet.appspot.com/resend_request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_email: user.email,
          domain: domain.domain,
 expiration_date: format(new Date(domain.expiry_date || ''), "MMM dd, yyyy"),
          days_remaining: domain.days_remaining,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send email notification");
      }
    } catch (err) {
      console.error("Failed to send email notification:", err);
    }
  };

  const formatDateTime = (date: Date) => {
    const formatter = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
    return `${formatter.format(date)} GMT`;
  };

  // Add a new domain
  const addDomain = async (domainName: string) => {
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.id);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error("User document not found");
      }

      const userData = userDoc.data() as UserData;
      // const subscriptionData = userData?.subscription;

      // Check if domain already exists
      if (userData.certificates.some((cert) => cert.domain === domainName)) {
        throw new Error("Domain already exists");
      }

      // Check certificate limit
      const limit = userData?.subscription?.certificate_limit || 3;
      if (userData.certificates.length >= limit) {
        throw new Error(`Certificate limit (${limit}) reached`);
      }

      const certInfo = await checkCertificateExpiration(domainName);

      let status: Certificate["status"] = "valid";
      if (certInfo.expired) {
        status = "expired";
      } else if (certInfo.days_remaining !== undefined && certInfo.days_remaining <= 7) {
        status = "warning";
      } else if (certInfo.days_remaining !== undefined && certInfo.days_remaining <= 30) {
        status = "warning";
      }

      const newCertificate: Certificate = {
        domain: domainName,
        serial_number: certInfo.serial_number,
        status: status,
        expired: certInfo.expired,
        expires_at: certInfo.expires_at,
        issuer: certInfo.issuer,
        last_checked: certInfo.last_checked,
        days_remaining: certInfo.days_remaining,
        date_added: formatDateTime(new Date()),
        expiry_date: certInfo.expires_at?.toString(),
        name: domainName,
        issued_at: certInfo.issued_at?.toString(),
        added_by: user.email?.toString() || "",
        subject: certInfo.subject || "", 
        subject_alternative_names: certInfo.subject_alternative_names || [], 
        public_key: certInfo.public_key || "",
        chain_length: certInfo.chain_length || "",
        signature_algorithm: certInfo.signature_algorithm || "",
        pem: certInfo.pem || "", 
        version: certInfo.version, 
        key_type: certInfo.key_type, 
        handshake_details: certInfo.handshake_details
      };
      // console.log("New certificate:", newCertificate);

      // Update the certificates array
      const updatedCertificates = [...userData.certificates, newCertificate];

      await updateDoc(userRef, { certificates: updatedCertificates});

      setDomains(updatedCertificates);

      // Send email notification
      await sendDomainAddedEmail(newCertificate);

      return newCertificate;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add domain");
      throw err;
    }
  };

  // Remove a domain
  const removeDomain = async (domainName: string) => {
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.id);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error("User document not found");
      }

      const userData = userDoc.data() as UserData;
      const updatedCertificates = userData.certificates.filter((cert) => cert.domain !== domainName);

      await updateDoc(userRef, { certificates: updatedCertificates });
      setDomains(updatedCertificates);
    } catch (err) {
      setError("Failed to remove domain");
      throw err;
    }
  };

  // Refresh a domain's SSL status
  const refreshDomain = async (domainName: string, serial_number?: string) => {
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.id);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error("User document not found");
      }

      const userData = userDoc.data() as UserData;
      const certInfo = (serial_number === undefined) ? await checkCertificateExpiration(domainName) : await checkCertificateExpiration(domainName, serial_number);

      let status: Certificate["status"] = "valid";
      if (certInfo.expired) {
        status = "expired";
      } else if (certInfo.days_remaining !== undefined && certInfo.days_remaining <= 7) {
        status = "warning";
      } else if (certInfo.days_remaining !== undefined && certInfo.days_remaining <= 30) {
        status = "warning";
      }

      const updatedCertificates = userData.certificates.map((cert) => {
        if (cert.domain === domainName) {
          return {
            ...cert,
            ...certInfo,
            status,
            last_checked: certInfo.last_checked,
            days_remaining: certInfo.days_remaining,
            expiry_date: certInfo.expires_at?.toString(),
            issued_at: certInfo.issued_at?.toString(),
            public_key: certInfo.public_key ?? "",
            chain_length: certInfo.chain_length ?? "",
            signature_algorithm: certInfo.signature_algorithm ?? "",
            pem: certInfo.pem ?? "",
            version: certInfo.version ?? 0,
            key_type: certInfo.key_type ?? "",
            handshake_details: certInfo.handshake_details ?? "",
            subject: certInfo.subject ?? "",
            subject_alternative_names: certInfo.subject_alternative_names ?? [],
            intermediate_cert: certInfo.intermediate_cert ?? null,
            root_cert: certInfo.root_cert ?? null,
            name: cert.name ?? domainName,
            added_by: cert.added_by ?? (user.email?.toString() || ""),
            date_added: cert.date_added ?? "",
          };
        }
        return cert;
      });

      await updateDoc(userRef, { certificates: updatedCertificates });
      setDomains(updatedCertificates as Certificate[]);
    } catch (err) {
      setError("Failed to refresh domain");
      throw err;
    }
  };

  return {
    domains,
    isLoading,
    error,
    addDomain,
    removeDomain,
    refreshDomain,
  };
};

export type { Certificate };

export const useEmailNotifications = () => {
  const { user } = useAuth();
  const [emailNotifications, setEmailNotifications] = useState(false);

  useEffect(() => {
    const fetchNotificationSettings = async () => {
      if (!user) return;

      try {
        const userRef = doc(db, "users", user.id);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setEmailNotifications(userData.notifications?.email || false);
        }
      } catch (err) {
        console.error("Failed to fetch notification settings:", err);
      }
    };

    fetchNotificationSettings();
  }, [user]);

  const toggleEmailNotifications = async (enabled: boolean) => {
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.id);
      await updateDoc(userRef, {
        "notifications.email": enabled,
      });
      setEmailNotifications(enabled);
    } catch (err) {
      console.error("Failed to update email notifications:", err);
    }
  };

  return {
    emailNotifications,
    toggleEmailNotifications,
  };
};

export const useNotificationSettings = () => {
  const { user } = useAuth();
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [weeklySummary, setWeeklySummary] = useState(false);

  useEffect(() => {
    const fetchNotificationSettings = async () => {
      if (!user) return;

      try {
        const userRef = doc(db, "users", user.id);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setSmsNotifications(userData.notifications?.sms || false);
          setWeeklySummary(userData.notifications?.weeklySummary || false);
        }
      } catch (err) {
        console.error("Failed to fetch notification settings:", err);
      }
    };

    fetchNotificationSettings();
  }, [user]);

  const toggleSmsNotifications = async (enabled: boolean) => {
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.id);
      await updateDoc(userRef, {
        "notifications.sms": enabled,
      });
      setSmsNotifications(enabled);
    } catch (err) {
      console.error("Failed to update SMS notifications:", err);
    }
  };

  const toggleWeeklySummary = async (enabled: boolean) => {
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.id);
      await updateDoc(userRef, {
        "notifications.weeklySummary": enabled,
      });
      setWeeklySummary(enabled);
    } catch (err) {
      console.error("Failed to update weekly summary notifications:", err);
    }
  };

  return {
    smsNotifications,
    weeklySummary,
    toggleSmsNotifications,
    toggleWeeklySummary,
  };
};
